import {
  type BookMotionRect,
  type Cover,
  type FlipPageParams,
  type Page,
  type PageSideContent,
  type PointerPosition,
} from '../../components/PhotoBook/PhotoBookProvider/PhotoBook.types'
import { isPhotoCover } from '../../components/PhotoBook/PhotoBookProvider/PhotoBook.utils'
import { SWIPE_MIN_DISTANCE } from '../../components/PhotoBook/PhotoBookView/Book3D/book.consts'
import { type Photo } from '../../components/PhotoBank/PhotoBank.types'
import { PHOTO_BOOK_HOMEPAGE_URL, PHOTO_BOOK_URL } from '../../config/config'
import { type Crop } from '../../utils/cropUtils.types'
import { getPhotoBooksStore } from 'stores/RootStoreProvider'
import { readOutsideReaction } from '../../utils/mobx/readOutsideReaction'

export function getPhotoUrl(
  photo: Pick<Photo, 'highResUrl' | 'minimalResUrl'> | undefined,
  showHighRes: boolean
) {
  if (!photo) {
    return undefined
  }

  if (showHighRes) {
    return photo.highResUrl
  }

  return photo.minimalResUrl
}

export function isBlankPageSide(page: PageSideContent) {
  return page.photos.length === 0
}

export function photosInPages(pages: Page[]) {
  return pages.flatMap((page) => [...page.front.photos, ...page.back.photos])
}

export function getBookPhotos(cover: Cover, pages: Page[]) {
  const pagePhotos = photosInPages(pages)
  return isPhotoCover(cover) ? [cover.photo, ...pagePhotos] : pagePhotos
}

export function getBookPhotosUids(cover: Cover, pages: Page[]) {
  return getBookPhotos(cover, pages).map((photo) => photo.uid)
}

export function getGpsCoordsFromPages(pages: Page[]) {
  return photosInPages(pages)
    .map((p) => p.gps)
    .filter((gps): gps is NonNullable<typeof gps> => gps != null)
}

export const getPhotoBookURL = () => {
  return readOutsideReaction(() => getPhotoBooksStore().hasAnyBook)
    ? PHOTO_BOOK_URL
    : PHOTO_BOOK_HOMEPAGE_URL
}

export function didUserFlip(source: FlipPageParams['source']): boolean {
  const userActions: FlipPageParams['source'][] = [
    'Swipe',
    'Arrow Key',
    'Arrow Button',
  ]
  return userActions.includes(source)
}

export function isPositionInsideBook(
  x: number,
  y: number,
  bookMotionRect: BookMotionRect
) {
  return (
    x < bookMotionRect.right.get() &&
    x > bookMotionRect.left.get() &&
    y > bookMotionRect.top.get() &&
    y < bookMotionRect.bottom.get()
  )
}

export function isTapOutsideBook(
  downPosition: PointerPosition,
  upPosition: PointerPosition,
  bookMotionRect: BookMotionRect
): boolean {
  const movedDistance = Math.hypot(
    upPosition.x - downPosition.x,
    upPosition.y - downPosition.y
  )
  return (
    movedDistance < SWIPE_MIN_DISTANCE &&
    !isPositionInsideBook(downPosition.x, downPosition.y, bookMotionRect) &&
    !isPositionInsideBook(upPosition.x, upPosition.y, bookMotionRect)
  )
}

export function isCropMatted(cropParams: Crop): boolean {
  return cropParams?.width > 1 && cropParams?.height > 1
}

export function isPageInWindow(
  pageIndex: number,
  currentPageIndex: number,
  windowSize: number
): boolean {
  return Math.abs(pageIndex - currentPageIndex) <= windowSize
}
